import { parseDate } from 'lib/date';
import { RetrievalMode } from 'services/api/portalAutomationSettings';
import {
  PortalConnectionBlocker,
  PortalConnectionCredentialsOptions,
  PortalConnectionOptions,
  PortalConnectionRecordingOptions,
} from 'services/api/portalConnections';
import { PortalScriptPurpose } from 'services/api/portalScripts';

export interface PortalConnectionCredentials {
  status: 'active' | 'invalid' | null;
  username: string | null;
  savedAt: Date | null;
  verifiedAt: Date | null;
}

export interface PortalConnectionRecording {
  recorded: boolean;
  recordedAt: Date | null;
  owned: boolean;
}

function toCredentials(
  options: Partial<PortalConnectionCredentialsOptions> | null
): PortalConnectionCredentials | null {
  if (!options) return null;

  return {
    status: options.status ?? null,
    username: options.username ?? null,
    savedAt: parseDate(options.savedAt) ?? null,
    verifiedAt: parseDate(options.verifiedAt) ?? null,
  };
}

function toRecording(options?: Partial<PortalConnectionRecordingOptions>): PortalConnectionRecording {
  return {
    recorded: options?.recorded ?? false,
    recordedAt: parseDate(options?.recordedAt) ?? null,
    owned: options?.owned ?? false,
  };
}

/**
 * @class PortalConnection
 * @classdesc One configured portal as an end user sees it — the "Connected Portal" of the
 * redesign. Readiness (`ready` / `blockers` / `canScan` / `scanBlockers`) is carried, never
 * recomputed: the api derives it once for both the dispatch gate and this payload.
 */
export default class PortalConnection {
  sourceSystem: string;
  displayName: string;
  retrievalMode: RetrievalMode;
  credentials: PortalConnectionCredentials | null;
  recordings: Record<PortalScriptPurpose, PortalConnectionRecording>;
  lastScanAt: Date | null;
  liveScanWorkflowId: string | null;
  ready: boolean;
  blockers: PortalConnectionBlocker[];
  canScan: boolean;
  scanBlockers: PortalConnectionBlocker[];

  constructor(options: Partial<PortalConnectionOptions> = {}) {
    this.sourceSystem = options.sourceSystem ?? '';
    // Falls back to the identifier rather than to a hardcoded map: an unlabeled portal is a
    // server-side gap, and spelling it out here is how the map came back last time.
    this.displayName = options.displayName || this.sourceSystem;
    this.retrievalMode = options.retrievalMode ?? 'manual';
    this.credentials = toCredentials(options.credentials ?? null);
    this.recordings = {
      retrieval: toRecording(options.recordings?.retrieval),
      discovery: toRecording(options.recordings?.discovery),
    };
    this.lastScanAt = parseDate(options.lastScanAt) ?? null;
    this.liveScanWorkflowId = options.liveScanWorkflowId ?? null;
    this.ready = options.ready ?? false;
    this.blockers = options.blockers ?? [];
    this.canScan = options.canScan ?? false;
    this.scanBlockers = options.scanBlockers ?? [];
  }

  recording(purpose: PortalScriptPurpose): PortalConnectionRecording {
    return this.recordings[purpose];
  }

  get isHeadless() {
    return this.retrievalMode === 'headless';
  }

  get isManual() {
    return this.retrievalMode === 'manual';
  }

  // A sweep is running right now — at most one per portal (the api's singleton rule).
  get isScanning() {
    return !!this.liveScanWorkflowId;
  }
}
